import React, { FC, useCallback, useState } from 'react'
import useTableTranslation from '@components/UI-Components/HierarchyWiseItemsTable/hooks/use-table-translation'
import { Column } from '@components/UI-Components/HierarchyWiseItemsTable/models/column'
import { useObserver } from 'mobx-react-lite'
import useOrderContext from '@modules/order/hooks/use-order-context'
import useViewContext from '@modules/order/hooks/use-view-context'
import { nav } from '@stores/nav'
import { productConfiguration } from '@stores/productConfigurationStore'
import { productCurveStore } from '@stores/productCurvesStore'
import { OrderType } from '@api/common'
import { getEnumValue } from '@utils/get-enum-value'
import { VIPClientType } from '@modules/common/models/enums/VIPClientType'
import { View } from '@modules/order/constants/views'
import useHierarchyItemTableContext from '@components/UI-Components/HierarchyWiseItemsTable/hooks/use-hierarchy-item-table-context'
import { CentricTypography } from '@core/component/CentricTypography'
import { EnhancedListUIStore } from '@modules/showroom/EnhancedOrder/stores/EnhancedListView/EnhancedListUIStore'
import EnhancedProductQuantitiesUIStore from '@modules/showroom/productDetail/EnhancedProductQuantities/stores/EnhancedProductQuantitiesUIStore'
import { EnhancedListDataStore } from '@modules/showroom/EnhancedOrder/stores/EnhancedListView/EnhancedListDataStore'
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  makeStyles
} from '@material-ui/core'
import { SecondaryButton } from '@components/UI-Components/SecondaryButton'
import {
  CURVE_TYPES,
  ROLL_UP_OPERATIONS_LIST,
  ROLLUP_OPERATIONS_IDS,
  SPLIT_OPERATIONS_LIST,
  SPLIT_OPERATIONS_IDS
} from '@modules/showroom/EnhancedOrder/constants/uiConstants'
import RollupSplitActions from '../EnhancedListProductQuantity/RollUpAndSplitPopUp/RollupSplitActions'

export interface CellProps {
  column?: Column
  customClass?: any
}
const useStyles = makeStyles(() => ({
  bodyContainer: {
    display: 'flex',
    gap: 20,
    maxHeight: 400,
    flexDirection: 'column',
    overflowY: 'unset'
  },
  productIdSection: {
    overflowY: 'auto',
    wordWrap: 'break-word',
    paddingRight: 15,
    textAlign: 'justify'
  },
  btn: {
    height: 29,
    padding: '0px 4px',
    display: 'flex',
    alignItems: 'center'
  },
  disabledBtn: {
    opacity: 0.5,
    cursor: 'not-allowed'
  },
  qtyCellWrapper: {
    height: 30,
    display: 'flex',
    alignItems: 'center',
    borderBottom: '1px solid #ccc',
    justifyContent: 'center',
    gap: 10
  },
  enhancedCellWrapper: {
    height: 30,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#eeeeee',
    borderRight: '1px solid #ccc',
    borderBottom: '1px solid #ccc'
  },
  cell: {
    display: '-webkit-box',
    overflow: 'hidden',
    textAlign: 'center',
    '-webkit-line-clamp': 2,
    '-webkit-box-orient': 'vertical'
  },
  splitORRollUpLabelButton3: {
    fontWeight: 600,
    lineHeight: '1.25rem',
    letterSpacing: '0.02em',
    textAlign: 'center'
  }
}))

const RollUpButton: () => FC<CellProps> =
  () =>
  ({ column }) =>
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useObserver(function useHook() {
      const {
        uiStore: {
          isOrderEditable,
          dataStore: {
            summary: { client, clientOrder }
          }
        }
      } = useOrderContext()
      const viewContextVal = useViewContext()
      const { t } = useTableTranslation()
      const classes = useStyles()
      const [openDialog, setOpenDialog] = useState(() => ({
        open: false,
        curveTypeToBeUsed: CURVE_TYPES.DOOR_CURVE
      }))
      const {
        queryParams: { clientId, buyingSessionGroupId, orderType, clientType, view }
      } = nav
      const { hideSplitQuantity } = productConfiguration
      // eslint-disable-next-line react-hooks/rules-of-hooks
      const productDetailsContextVal = useHierarchyItemTableContext()
      const uiStore = productDetailsContextVal.store as EnhancedProductQuantitiesUIStore

      const quantitiesUiStore =
        view === View.List
          ? (viewContextVal.uiStore as EnhancedListUIStore<EnhancedListDataStore>)
          : uiStore

      const {
        rollUpAllQuantities,
        splitAllQuantities,
        areObjectsDataLoading,
        dataStore: {
          productsWithoutCurves,
          setProductsWithoutCurves,
          isSplittingInProgress
        }
      } = quantitiesUiStore

      const hasGradeCurves = productCurveStore.hasGradeCurves(
        clientId,
        buyingSessionGroupId
      )

      const hasDoorCurves = productCurveStore.hasDoorCurves(
        clientId,
        buyingSessionGroupId
      )

      const hasSizeCurves = productCurveStore.hasSizeCurves(
        clientId,
        buyingSessionGroupId
      )
      const splitQuantityHandler = useCallback(
        (splitProductToDoors, splitDoorsToSizes) => {
          let curveTypeToBeUsed = splitDoorsToSizes
            ? CURVE_TYPES.SIZE_CURVE
            : CURVE_TYPES.DOOR_CURVE

          const defaultSplit = !splitProductToDoors && !splitDoorsToSizes
          const selectedCurveType = splitDoorsToSizes ? hasSizeCurves : hasDoorCurves
          if (selectedCurveType) {
            splitAllQuantities(
              defaultSplit || splitProductToDoors,
              defaultSplit || splitDoorsToSizes,
              curveTypeToBeUsed
            )
          } else {
            setOpenDialog(prevState => ({
              ...prevState,
              open: true,
              curveTypeToBeUsed: curveTypeToBeUsed
            }))
          }
        },
        [hasDoorCurves, hasSizeCurves, splitAllQuantities]
      )
      const rollUpQuantitiesHandler = useCallback(
        (rollUpSizeToDoor, rollUpDoorToProduct) => {
          const defaultSplit = !rollUpSizeToDoor && !rollUpDoorToProduct
          rollUpAllQuantities(
            defaultSplit || rollUpSizeToDoor,
            defaultSplit || rollUpDoorToProduct
          )
        },
        [rollUpAllQuantities]
      )

      const handleDialogClose = useCallback(() => {
        setProductsWithoutCurves({
          productsWithoutDoorCurve: [],
          productsWithoutSizeCurve: [],
          curveTypeToBeUsed: CURVE_TYPES.DOOR_CURVE
        })
        openDialog &&
          setOpenDialog(prevState => ({
            ...prevState,
            open: false
          }))
      }, [openDialog, setProductsWithoutCurves])

      const { label, field } = column
      const isDoorOrder = orderType === OrderType.Door
      const isRetailView = clientType === getEnumValue(VIPClientType.Internal)
      const isGradingEnabled = !isRetailView && hasGradeCurves && !isDoorOrder
      const captureSize = client?.captureSize || clientOrder?.client?.captureSize
      const fallBackProductList =
        productsWithoutCurves?.curveTypeToBeUsed === CURVE_TYPES.DOOR_CURVE
          ? productsWithoutCurves?.productsWithoutDoorCurve
          : productsWithoutCurves?.productsWithoutSizeCurve
      const disableSplittingQuantities =
        areObjectsDataLoading ||
        !isOrderEditable ||
        isSplittingInProgress ||
        productCurveStore.getCurveLoading(clientId, buyingSessionGroupId)

      return (
        <>
          <div className={classes.qtyCellWrapper}>
            {!isDoorOrder && !isGradingEnabled && (
              <div>
                <RollupSplitActions
                  isIconButton={false}
                  buttonLabel={t(`order.rollUpAll`, 'Roll Up All')}
                  disableButton={disableSplittingQuantities}
                  menuOptions={ROLL_UP_OPERATIONS_LIST}
                  onActionClick={optionId => {
                    if (disableSplittingQuantities) {
                      return
                    }
                    rollUpQuantitiesHandler(
                      optionId === ROLLUP_OPERATIONS_IDS.SIZES_TO_DOORS,
                      optionId === ROLLUP_OPERATIONS_IDS.DOORS_TO_PRODUCT || !optionId
                    )
                  }}
                  isCaptureSize={captureSize}
                />
              </div>
            )}
            {!isDoorOrder && (isRetailView || !hideSplitQuantity) && (
              <div>
                <RollupSplitActions
                  isIconButton={false}
                  buttonLabel={t(`order.splitAll`, 'Split All')}
                  disableButton={disableSplittingQuantities}
                  menuOptions={SPLIT_OPERATIONS_LIST}
                  onActionClick={optionId => {
                    if (disableSplittingQuantities) {
                      return
                    }
                    splitQuantityHandler(
                      optionId === SPLIT_OPERATIONS_IDS.PRODUCTS_TO_DOORS || !optionId,
                      optionId === SPLIT_OPERATIONS_IDS.DOORS_TO_SIZES
                    )
                  }}
                  isCaptureSize={captureSize}
                />
              </div>
            )}
          </div>
          <div className={classes.enhancedCellWrapper}>
            <span className={classes.cell}>
              <CentricTypography variant={'subtitle2DarkGrey'}>
                {t(`column.header.${field}.label`, label)}
              </CentricTypography>
            </span>
          </div>

          {Boolean(fallBackProductList.length || openDialog.open) ? (
            <Dialog
              open={Boolean(fallBackProductList.length) || openDialog.open}
              onClose={handleDialogClose}
            >
              <DialogTitle id='alert-dialog-title'>
                {openDialog.open
                  ? t('common.label.notAllowed', 'Not Allowed')
                  : t('common.label.splitCompleted', 'Split Completed')}
              </DialogTitle>
              <DialogContent className={classes.bodyContainer}>
                <CentricTypography variant='body1Black'>
                  {openDialog?.open
                    ? openDialog?.curveTypeToBeUsed === CURVE_TYPES.DOOR_CURVE
                      ? t(
                          'order.messages.splitToDoorErrorMessage',
                          'Sorry, but there is no door distribution information available, please contact your showroom administrator for help.'
                        )
                      : t(
                          'order.messages.splitToSizeErrorMessage',
                          'Sorry, but there is no size distribution information available, please contact your showroom administrator for help.'
                        )
                    : `Split has been completed however there is no ${
                        productsWithoutCurves?.curveTypeToBeUsed ===
                        CURVE_TYPES.DOOR_CURVE
                          ? 'door'
                          : 'size'
                      } distribution information available for the following product${
                        fallBackProductList.length > 1 ? 's' : ''
                      }:`}
                </CentricTypography>

                {!openDialog?.open ? (
                  <CentricTypography className={classes.productIdSection} variant='body1'>
                    {fallBackProductList.join(', ')}
                  </CentricTypography>
                ) : null}
              </DialogContent>
              <DialogActions style={{ padding: 15 }}>
                <SecondaryButton onClick={handleDialogClose}>
                  {t('common.label.ok', 'Ok')}
                </SecondaryButton>
              </DialogActions>
            </Dialog>
          ) : null}
        </>
      )
    })

export default RollUpButton
